/**
 * MASTER SHEET FORM - FIXED
 */

import React, { useState, useRef, useEffect, useMemo, useLayoutEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Sheet, Edit } from 'lucide-react';
import { motion } from 'framer-motion';
import { UniversalTable, useTableData, useTableNavigation, useAutoScroll, DEFAULT_STYLE_CONFIG } from '../../../universal_table';
import { usePersistentTableData } from '../../../universal_table/hooks/usePersistentTableData';
import { FormActions, StatusBanner, CorrectMistakeModal, DateFilter, DateRangeFilter } from '../../../shared';
import { DataAggregationModal } from './DataAggregationModal';
import { MASTER_SHEET_COLUMNS } from '../forms_data/masterSheetColumns';
import { masterSheetService, MasterSheetDataRow, loadAllPaginatedData } from '../services/axonApiService';
import { useAuth } from '../../../../../../context/AuthContext';
import { buildExcelUploadStorageKey, buildUserScopedStorageKey } from '../utils/storageKeys';
import { resolveUserFilterId } from '../utils/userDataAccess';
import { buildGenerationReportFooterText } from '../utils/generationReportFooter';
import backendApi from '../../../../../../services/backendApi';

interface MasterSheetFormProps {
  site?: string;
}

export const MasterSheetForm: React.FC<MasterSheetFormProps> = ({ site: siteProp }) => {
  const MST_DEBUG = false; // âš¡ Set true for debug logging, false for production speed
  const { site: siteFromParams } = useParams<{ site: string }>();
  const site = siteProp || siteFromParams;
  const siteCode = (site || 'axon').toLowerCase();
  const [isCondensed, setIsCondensed] = useState(true);
  const [status, setStatus] = useState<any>({ type: null, message: '' });
  const [isExporting, setIsExporting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [showCorrectMistake, setShowCorrectMistake] = useState(false);
  const [correctMistakeData, setCorrectMistakeData] = useState<MasterSheetDataRow[]>([]);
  const [loadingCorrectMistake, setLoadingCorrectMistake] = useState(false);
  const [isAggregating, setIsAggregating] = useState(false);
  const [siteName, setSiteName] = useState<string>('axon');
  // Range Filter State
  const [startDate, setStartDate] = useState<string | null>(null);
  const [endDate, setEndDate] = useState<string | null>(null);
  const [showAggregationModal, setShowAggregationModal] = useState(false);
  const { user } = useAuth();

  const containerRef = useRef<HTMLDivElement>(null);
  const hasScrolledToBottomRef = useRef(false); // Track if we've scrolled to bottom on initial load
  const [isTableVisible, setIsTableVisible] = useState(false); // Hide table until scroll is positioned
  const oldestLoadedDateRef = useRef<string | null>(null); // Track oldest date we've loaded for lazy loading
  const isLoadingMoreRef = useRef(false); // Track if we're currently loading more data
  const hasMoreDataRef = useRef(true); // Track if there's more data to load
  const SCROLL_TOP_LOAD_THRESHOLD = 260;
  // Scroll restore ref: set before prepending rows, consumed by useLayoutEffect
  const scrollRestoreRef = useRef<{ scrollTop: number; scrollHeight: number } | null>(null);

  // Clean up empty persisted data BEFORE hook initializes
  // This runs synchronously during component initialization, before the hook tries to restore data
  const storageKey = useMemo(
    () => buildUserScopedStorageKey('unsaved_masterSheet', siteCode, user),
    [siteCode, user?.username, user?.email, user?.fullName, user?.siteId]
  );
  (() => {
    try {
      const persistedData = localStorage.getItem(storageKey);
      if (persistedData) {
        const parsed = JSON.parse(persistedData);
        if (parsed && parsed.data && parsed.rowKeys && parsed.rowKeys.length > 0) {
          // Check if all rows are empty
          const allRowsEmpty = parsed.rowKeys.every((key: string) => {
            const row = parsed.data[key];
            if (!row) return true;
            // Check if row has any non-empty values (excluding entry_date and id)
            const hasData = Object.keys(row).some(k =>
              k !== 'entry_date' &&
              k !== 'id' &&
              row[k] !== null &&
              row[k] !== undefined &&
              row[k] !== '' &&
              row[k] !== '0'
            );
            return !hasData;
          });

          if (allRowsEmpty) {
localStorage.removeItem(storageKey);
          }
        }
      }
    } catch (error) {
}
  })();

  const { data, rowKeys, updateCell, addRow, removeRow, updateRowKey, setAllData } = usePersistentTableData(
    {},
    [],
    { storageKey, enabled: true, debounceMs: 500 }
  );

  const footerText = useMemo(
    () => buildGenerationReportFooterText({
      siteName,
      sheetName: 'Daily Report',
      dateKeys: rowKeys
    }),
    [siteName, rowKeys]
  );

  // Filter rowKeys by selected date range
  const filteredRowKeys = useMemo(() => {
    if (!startDate && !endDate) return rowKeys;

    return rowKeys.filter(key => {
      const rowDate = String(data[key]?.entry_date || key).split('T')[0];

      // If only start date is selected
      if (startDate && !endDate) {
        return rowDate >= startDate;
      }

      // If only end date is selected
      if (!startDate && endDate) {
        return rowDate <= endDate;
      }

      // If both are selected
      if (startDate && endDate) {
        return rowDate >= startDate && rowDate <= endDate;
      }

      return true;
    });
  }, [rowKeys, data, startDate, endDate]);

  // Load data from backend when date filter changes
  useEffect(() => {
    if (!startDate && !endDate) return; // No filter applied
    if (isInitialLoad) return; // Don't trigger during initial load

    let isCancelled = false;

    const loadFilteredData = async () => {
      try {
        const userResponse = await backendApi.getCurrentUser();
        if (!userResponse.success || !userResponse.user?.id || isCancelled) return;

        const backendUser = userResponse.user;
        const resolvedUserId = resolveUserFilterId(user, backendUser);
        const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;
let loadedData: any = {};
        let loadedKeys: string[] = [];

        const rows = await loadAllPaginatedData(
          (params) => masterSheetService.loadByDateRange({
            userId: userIdForLoad,
            siteCode: siteCode,
            siteId: user?.siteId || backendUser?.site?.site_id || null,
            startDate: params.startDate,
            endDate: params.endDate,
            offset: params.offset,
            limit: params.limit
          }) as any,
          {
            startDate: startDate || undefined,
            endDate: endDate || undefined,
            initialLimit: 200
          }
        );

        if (isCancelled) return;

        if (rows.length > 0) {
          rows.forEach((row: any) => {
            const dateKey = row.entry_date;
            if (dateKey) {
              const mappedRow: any = { entry_date: dateKey, id: row.id || row.pk || undefined };
              MASTER_SHEET_COLUMNS.forEach(col => {
                if (col.key !== 'entry_date') {
                  const value = row[col.key];
                  mappedRow[col.key] = value !== null && value !== undefined ? String(value) : '';
                }
              });
              loadedData[dateKey] = mappedRow;
              if (!loadedKeys.includes(dateKey)) loadedKeys.push(dateKey);
            }
          });
        }

        if (isCancelled) return;
        loadedKeys.sort();
        setAllData(loadedData, loadedKeys);
} catch (error) {
        console.error('Error loading filtered data:', error);
      }
    };

    loadFilteredData();
    return () => {
      isCancelled = true;
    };
  }, [startDate, endDate]);
  const { scrollToCell, registerInput } = useAutoScroll(containerRef);
  const { focusedCell, setFocusedCell, handleKeyDown } = useTableNavigation(
    rowKeys.length,
    MASTER_SHEET_COLUMNS.length,
    MASTER_SHEET_COLUMNS,
    scrollToCell
  );
  // Stable reference so memoized table rows aren't forced to re-render just because
  // this closure was recreated (setFocusedCell itself is already stable).
  const handleTableFocus = useCallback(
    (r: number, c: number) => setFocusedCell({ rowIndex: r, colIndex: c }),
    [setFocusedCell]
  );

  // ✅ Added adapter functions for index-based callbacks
  const handleRowKeyChange = (index: number, newKey: string) => {
    const oldKey = rowKeys[index];
    updateRowKey(oldKey, newKey);
  };

  const handleRemoveRow = (index: number) => {
    const keyToRemove = rowKeys[index];
    removeRow(keyToRemove);
  };

  /**
   * Create empty row with all fields initialized
   */
  const createEmptyRow = (date: string) => {
    const emptyRow: any = { entry_date: date };
    MASTER_SHEET_COLUMNS.forEach(col => {
      if (col.key !== 'entry_date') {
        emptyRow[col.key] = '';
      }
    });
    return emptyRow;
  };

  // Fetch site name from database
  useEffect(() => {
    const fetchSiteName = async () => {
      try {
        const response = await backendApi.getSites(false, siteCode.toUpperCase());
        if (response.success && Array.isArray(response.sites)) {
          const matchedSite = response.sites.find(
            (item: any) => item.site_code && item.site_code.toUpperCase() === siteCode.toUpperCase()
          ) || response.sites[0];

          if (matchedSite && matchedSite.site_name) {
            setSiteName(matchedSite.site_name);
          } else {
            setSiteName(siteCode.toUpperCase());
          }
        }
      } catch (error) {
setSiteName(siteCode.toUpperCase());
      }
    };

    fetchSiteName();
  }, [siteCode]);

  /**
   * Initialize by loading user's existing data or Excel uploaded data
   * IMPORTANT: Check for persisted unsaved data first to preserve user's work
   */
  useEffect(() => {
    if (!isInitialLoad) return;

    const loadUserData = async () => {
      try {
        // First, check if persisted data was already restored by usePersistentTableData hook
        // The hook now filters out empty rows, so if rowKeys has content, it's meaningful
        // But double-check to be safe
        if (rowKeys.length > 0) {
          const hasMeaningfulData = rowKeys.some(key => {
            const row = data[key];
            if (!row) return false;
            // Check if row has any non-empty values (excluding entry_date and id)
            return Object.keys(row).some(k =>
              k !== 'entry_date' &&
              k !== 'id' &&
              row[k] !== null &&
              row[k] !== undefined &&
              row[k] !== ''
            );
          });

          if (hasMeaningfulData) {
            // Always load from backend to ensure we have the latest data (including newly uploaded data)
            // Persisted data is mainly for unsaved changes, not for replacing backend data
// Don't return - continue to load from backend
            // The backend data will be merged with persisted data if needed
          } else {
            // Persisted data exists but is empty - clear it
try {
              localStorage.removeItem(storageKey);
              setAllData({}, []); // Clear empty rows
            } catch (error) {
}
            // Continue to load from database or create today's row
          }
        }

        // Second, check for Excel uploaded data
        if (site) {
          const storageKey = buildExcelUploadStorageKey(site, user);
          const excelDataStr = localStorage.getItem(storageKey);

          if (excelDataStr) {
            try {
              const excelData = JSON.parse(excelDataStr);
              const masterSheetData = excelData['Master Sheet'];

              if (masterSheetData && Object.keys(masterSheetData).length > 0) {
                // Convert Excel data to form format
                const loadedData: any = {};
                const loadedKeys: string[] = [];

                Object.entries(masterSheetData).forEach(([dateKey, rowData]: [string, any]) => {
                  const mappedRow: any = { entry_date: dateKey };
                  MASTER_SHEET_COLUMNS.forEach(col => {
                    if (col.key !== 'entry_date') {
                      const value = rowData[col.key];
                      mappedRow[col.key] = value !== null && value !== undefined ? String(value) : '';
                    }
                  });
                  loadedData[dateKey] = mappedRow;
                  loadedKeys.push(dateKey);
                });

                // Sort dates
                loadedKeys.sort();
                setAllData(loadedData, loadedKeys);

                // Clear Excel data from localStorage after loading
                localStorage.removeItem(storageKey);

                setStatus({ type: 'success', message: `✅ Loaded ${loadedKeys.length} rows from Excel upload` });
setIsInitialLoad(false);
                return;
              }
            } catch (excelError) {
              console.error('Error parsing Excel data:', excelError);
            }
          }
        }

        // Third, if no persisted data, load from backend with LAZY LOADING
        const userResponse = await backendApi.getCurrentUser();
        if (userResponse.success && userResponse.user?.id) {
          const backendUser = userResponse.user;
          const resolvedUserId = resolveUserFilterId(user, backendUser);
          const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

          let loadedData: any = {};
          let loadedKeys: string[] = [];

          // Backend handles role-based access: admins get all site data, regular users get own data.
          const loadResult = await masterSheetService.loadByUser(userIdForLoad);
          if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
            loadResult.data.forEach((row: any) => {
              const dateKey = row.entry_date;
              if (dateKey) {
                const mappedRow: any = { entry_date: dateKey, id: row.id || row.pk || undefined };
                MASTER_SHEET_COLUMNS.forEach(col => {
                  if (col.key !== 'entry_date') {
                    const value = row[col.key];
                    mappedRow[col.key] = value !== null && value !== undefined ? String(value) : '';
                  }
                });
                loadedData[dateKey] = mappedRow;
                if (!loadedKeys.includes(dateKey)) loadedKeys.push(dateKey);
              }
            });
          }

          if (Object.keys(loadedData).length > 0) {
            loadedKeys.sort();
            const finalKeys = [...loadedKeys].sort();
            if (finalKeys.length > 0) {
              oldestLoadedDateRef.current = finalKeys[0];
              hasMoreDataRef.current = true;
            }
            setAllData(loadedData, finalKeys);
} else {
            setAllData({}, []);
}
        } else {
          setAllData({}, []);
}
      } catch (error: any) {
        console.error('âŒ Error loading user data:', error);
        // On error, set empty data
        setAllData({}, []);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadUserData();
  }, [isInitialLoad, setAllData, site]);

  /**
   * Load more older data when user scrolls up (lazy loading)
   */
  const loadMoreOlderData = async () => {
    if (isLoadingMoreRef.current || !hasMoreDataRef.current || !oldestLoadedDateRef.current) {
      return;
    }

    isLoadingMoreRef.current = true;

    try {
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        isLoadingMoreRef.current = false;
        return;
      }

      const backendUser = userResponse.user;
      const resolvedUserId = resolveUserFilterId(user, backendUser);
      const userIdForLoad = (resolvedUserId ?? Number(backendUser.id)) || 0;

      // Calculate date range: 30 days before oldest loaded date
      const endDate = new Date(oldestLoadedDateRef.current);
      endDate.setDate(endDate.getDate() - 1);
      const startDate = new Date(endDate);
      startDate.setDate(startDate.getDate() - 30);
      const start = startDate.toISOString().split('T')[0];
      const end = endDate.toISOString().split('T')[0];

      let newData: any = {};
      let newKeys: string[] = [];

      const rows = await loadAllPaginatedData(
        (params) => masterSheetService.loadByDateRange({
          userId: userIdForLoad,
          siteCode: siteCode,
          siteId: user?.siteId || backendUser?.site?.site_id || null,
          startDate: params.startDate,
          endDate: params.endDate,
          offset: params.offset,
          limit: params.limit
        }) as any,
        {
          startDate: start,
          endDate: end,
          initialLimit: 200
        }
      );

      rows.forEach((row: any) => {
        const dateKey = row.entry_date;
        if (dateKey && dateKey < oldestLoadedDateRef.current!) {
          const mappedRow: any = { entry_date: dateKey, id: row.id || row.pk || undefined };
          MASTER_SHEET_COLUMNS.forEach(col => {
            if (col.key !== 'entry_date') {
              const value = row[col.key];
              mappedRow[col.key] = value !== null && value !== undefined ? String(value) : '';
            }
          });
          newData[dateKey] = mappedRow;
          if (!newKeys.includes(dateKey)) newKeys.push(dateKey);
        }
      });

      if (newKeys.length > 0) {
        newKeys.sort();

        // Merge with existing data
        const currentData = { ...data };
        const currentKeys = [...rowKeys];

        newKeys.forEach(key => {
          if (!currentKeys.includes(key) && newData[key]) {
            currentData[key] = newData[key];
            currentKeys.push(key);
          }
        });

        // Sort and update
        currentKeys.sort();
        oldestLoadedDateRef.current = currentKeys[0];
        hasMoreDataRef.current = true;

        // Save scroll state BEFORE setAllData — useLayoutEffect will restore it after DOM commit
        if (containerRef.current) {
          scrollRestoreRef.current = {
            scrollTop: containerRef.current.scrollTop,
            scrollHeight: containerRef.current.scrollHeight,
          };
        }
        setAllData(currentData, currentKeys);
      } else {
        hasMoreDataRef.current = false;
      }
    } catch (error) {
      console.error('Error loading more Master Sheet data:', error);
    } finally {
      isLoadingMoreRef.current = false;
    }
  };

  /**
   * Auto-sort rows by date (ascending - oldest to newest, so newest appears at bottom)
   */
  useEffect(() => {
    if (isInitialLoad || rowKeys.length === 0) return;

    const sortedKeys = [...rowKeys].sort();
    const isAlreadySorted = JSON.stringify(sortedKeys) === JSON.stringify(rowKeys);

    if (!isAlreadySorted) {
      const sortedData: any = {};
      sortedKeys.forEach(key => {
        if (data[key]) {
          sortedData[key] = data[key];
        }
      });
      setAllData(sortedData, sortedKeys);
    }
  }, [rowKeys.join(','), isInitialLoad]);

  // Reset scroll flag when initial load starts
  useEffect(() => {
    if (isInitialLoad) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(false);
    }
  }, [isInitialLoad]);

  // Lazy loading: Detect scroll up and load more older data
  // Uses BOTH scroll + wheel events to handle cases where content doesn't fill viewport
  useEffect(() => {
    if (isInitialLoad || !containerRef.current) return;

    const container = containerRef.current;
    let scrollTimeout: NodeJS.Timeout;
    let wheelTimeout: NodeJS.Timeout;

    const triggerLoadMore = () => {
      if (!containerRef.current || isLoadingMoreRef.current || !hasMoreDataRef.current) return;
      loadMoreOlderData();
    };

    const handleScroll = () => {
      clearTimeout(scrollTimeout);
      scrollTimeout = setTimeout(() => {
        if (!containerRef.current || isLoadingMoreRef.current || !hasMoreDataRef.current) return;
        const scrollTop = container.scrollTop;
        if (scrollTop <= SCROLL_TOP_LOAD_THRESHOLD) {
          triggerLoadMore();
        }
      }, 150);
    };

    // Wheel event fires even when content doesn't overflow (not scrollable)
    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY >= 0) return; // Only react to scroll-up (deltaY < 0)
      if (isLoadingMoreRef.current || !hasMoreDataRef.current) return;
      const scrollTop = container.scrollTop;
      const isNotScrollable = container.scrollHeight <= container.clientHeight;
      if (scrollTop <= SCROLL_TOP_LOAD_THRESHOLD || isNotScrollable) {
        clearTimeout(wheelTimeout);
        wheelTimeout = setTimeout(() => triggerLoadMore(), 150);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('wheel', handleWheel, { passive: true });

    // Auto-fill: if content doesn't fill viewport after load, load more immediately
    const isNotScrollable = container.scrollHeight <= container.clientHeight;
    if (isNotScrollable && hasMoreDataRef.current && !isLoadingMoreRef.current && rowKeys.length > 0) {
      triggerLoadMore();
    }

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('wheel', handleWheel);
      clearTimeout(scrollTimeout);
      clearTimeout(wheelTimeout);
    };
  }, [isInitialLoad, rowKeys.length, data]);

  // Set initial scroll position to bottom SYNCHRONOUSLY before browser paints
  useLayoutEffect(() => {
    // Always show table headers - even when no data
    if (isInitialLoad || !containerRef.current) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(false);
      return;
    }

    // If no data, just show the empty table with headers
    if (rowKeys.length === 0) {
      hasScrolledToBottomRef.current = false;
      setIsTableVisible(true);
      return;
    }

    if (hasScrolledToBottomRef.current) return;

    const container = containerRef.current;
    const tbody = container.querySelector('tbody');
    const actualRowCount = tbody ? tbody.querySelectorAll('tr').length : 0;
    const expectedRowCount = rowKeys.length;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    const maxScroll = scrollHeight - clientHeight;
    const rowsRendered = actualRowCount > 0;
    const hasScrollableContent = maxScroll > 0 && scrollHeight > clientHeight;

    if (rowsRendered) {
      container.style.scrollBehavior = 'auto';
      const targetScroll = hasScrollableContent ? maxScroll : 0;
      container.scrollTop = targetScroll;
      const currentScroll = container.scrollTop;

      if (Math.abs(currentScroll - targetScroll) < 1 || currentScroll >= targetScroll - 5 || (!hasScrollableContent && currentScroll === 0)) {
        hasScrolledToBottomRef.current = true;
        setIsTableVisible(true);
      } else {
        requestAnimationFrame(() => {
          if (containerRef.current && !hasScrolledToBottomRef.current) {
            const retryMaxScroll = containerRef.current.scrollHeight - containerRef.current.clientHeight;
            const retryTarget = retryMaxScroll > 0 ? retryMaxScroll : 0;
            containerRef.current.scrollTop = retryTarget;
            hasScrolledToBottomRef.current = true;
            setIsTableVisible(true);
          }
        });
      }
    }
  }, [isInitialLoad, rowKeys.length, rowKeys.join(',')]);

  // Restore scroll position synchronously after React commits prepended rows to DOM.
  // useLayoutEffect fires after DOM mutations but BEFORE browser paint — no visible jump.
  useLayoutEffect(() => {
    if (!scrollRestoreRef.current || !containerRef.current || isInitialLoad) return;
    const { scrollTop, scrollHeight } = scrollRestoreRef.current;
    scrollRestoreRef.current = null;
    const diff = containerRef.current.scrollHeight - scrollHeight;
    if (diff > 0) {
      containerRef.current.style.scrollBehavior = 'auto';
      containerRef.current.scrollTop = scrollTop + diff;
    }
  }, [rowKeys.length, isInitialLoad]);

  const save = async () => {
    setIsSaving(true);
    try {
      // Get current user ID
      const userResponse = await backendApi.getCurrentUser();
      if (!userResponse.success || !userResponse.user?.id) {
        setStatus({ type: 'error', message: 'Unable to identify user. Please login again.' });
        setIsSaving(false);
        return;
      }
      const userId = userResponse.user.id;
      const savedDates: string[] = [];
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];

      // Rows with no local `id` would otherwise each trigger masterSheetService.save()'s
      // internal findByDate() -> a full loadByUser() history fetch, PER ROW (an N+1
      // network storm when saving several new rows at once). Instead, resolve existing
      // ids for the whole batch in a single upfront fetch, and pass the resolved id
      // through so save() takes the direct update() path without its own lookup.
      const keysNeedingIdLookup = rowKeys.filter(key => !data[key]?.id);
      const existingIdByDate = new Map<string, number>();
      if (keysNeedingIdLookup.length > 0) {
        const existingResult = await masterSheetService.loadByUser(Number(userId));
        if (existingResult.ok && existingResult.data) {
          existingResult.data.forEach((row: any) => {
            if (row.entry_date && row.id) {
              existingIdByDate.set(row.entry_date, row.id);
            }
          });
        }
      }

      for (const key of rowKeys) {
        const rowData = data[key];

        // Skip empty rows (no data except date)
        const hasData = Object.keys(rowData).some(k =>
          k !== 'entry_date' && k !== 'id' && rowData[k] !== null && rowData[k] !== undefined && rowData[k] !== ''
        );

        if (!hasData) {
continue;
        }

        // ✅ Ensure entry_date is set and include site_code - PRESERVE ID
        const payload = {
          ...rowData,
          entry_date: key, // The row key IS the date
          site_code: siteCode, // Add site_code for backend
          id: rowData.id || existingIdByDate.get(key) || undefined // Preserve/resolve ID if exists
        };

        try {
          const result = await masterSheetService.save(payload);
          if (result.ok && result.data) {
            savedDates.push(key);
            successCount++;

            // Update local data with returned ID if new record was created
            if (result.data.id && !rowData.id) {
              data[key] = { ...data[key], id: result.data.id };
            }
          } else {
            errorCount++;
            errors.push(`${key}: ${result.error || 'Save failed'}`);
          }
        } catch (error: any) {
          errorCount++;
          errors.push(`${key}: ${error.message}`);
        }
      }

      // Show status message
      if (errorCount === 0 && successCount > 0) {
        setStatus({ type: 'success', message: `✅ Saved ${successCount} record(s)!` });
      } else if (errorCount > 0 && successCount > 0) {
        setStatus({ type: 'warning', message: `⚠️Ã¯Â¸Â Saved ${successCount} record(s), ${errorCount} failed. ${errors.slice(0, 3).join('; ')}` });
      } else if (errorCount > 0) {
        setStatus({ type: 'error', message: `âŒ Failed to save: ${errors.slice(0, 3).join('; ')}` });
      } else if (successCount === 0) {
        setStatus({ type: 'warning', message: '⚠️Ã¯Â¸Â No data to save. Please enter some values.' });
        setIsSaving(false);
        return;
      }

      // Reload saved data - use get-dgr for admins (all data), loadByUser for regular users
      if (savedDates.length > 0) {
        let loadedData: any = {};
        let loadedKeys: string[] = [];

        // Backend handles role-based access; reload all accessible data after save.
        const loadResult = await masterSheetService.loadByUser(Number(userId));
        if (loadResult.ok && loadResult.data && loadResult.data.length > 0) {
          const savedData = loadResult.data.filter((row: any) => savedDates.includes(row.entry_date));
          savedData.forEach((row: any) => {
            const dateKey = row.entry_date;
            if (dateKey) {
              const mappedRow: any = { entry_date: dateKey, id: row.id || row.pk || undefined };
              MASTER_SHEET_COLUMNS.forEach(col => {
                if (col.key !== 'entry_date') {
                  const value = row[col.key];
                  mappedRow[col.key] = value !== null && value !== undefined ? String(value) : '';
                }
              });
              loadedData[dateKey] = mappedRow;
              if (!loadedKeys.includes(dateKey)) loadedKeys.push(dateKey);
            }
          });
        }

        // Merge with existing data to preserve any unsaved changes
        if (Object.keys(loadedData).length > 0) {
          const mergedData = { ...data, ...loadedData };
          const mergedKeys = [...new Set([...rowKeys, ...loadedKeys])].sort();
          setAllData(mergedData, mergedKeys);
        }
      }

      // Clear unsaved data from localStorage after successful save
      if (successCount > 0) {
        try {
          localStorage.removeItem(storageKey);
        } catch (error) {
}
      }

      setStatus({ type: 'success', message: 'Saved!' });
    } catch (e: any) {
      setStatus({ type: 'error', message: e.message });
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Sort rows by date (ascending order)
   * Handles date strings in YYYY-MM-DD format from calendar inputs
   * 
   * IMPORTANT: This is FRONTEND-ONLY sorting. It only affects the display order.
   * - Does NOT call any backend APIs
   * - Does NOT modify backend data
   * - Does NOT affect save/load operations (data is saved/loaded based on row content, not order)
   * - Only reorders the visual display of rows in the table
   */
  const sortRowsByDate = () => {
    if (rowKeys.length === 0) {
      setStatus({ type: 'warning', message: 'No rows to sort' });
      return;
    }

    // Sort rowKeys by date (ascending) - handle YYYY-MM-DD format strings
    const sortedKeys = [...rowKeys].sort((a, b) => {
      // Handle date strings in YYYY-MM-DD format
      // For YYYY-MM-DD format, string comparison works correctly
      if (/^\d{4}-\d{2}-\d{2}$/.test(a) && /^\d{4}-\d{2}-\d{2}$/.test(b)) {
        return a.localeCompare(b);
      }

      // Fallback to Date object comparison for other formats
      const dateA = new Date(a).getTime();
      const dateB = new Date(b).getTime();

      // Handle invalid dates
      if (isNaN(dateA) || isNaN(dateB)) {
        return a.localeCompare(b); // Fallback to string comparison
      }

      return dateA - dateB;
    });

    // Check if already sorted
    const isAlreadySorted = JSON.stringify(sortedKeys) === JSON.stringify(rowKeys);
    if (isAlreadySorted) {
      setStatus({ type: 'info', message: 'Rows are already sorted by date' });
      return;
    }

    // Create new data object with sorted keys (preserve all data)
    const sortedData: any = {};
    sortedKeys.forEach(key => {
      if (data[key]) {
        sortedData[key] = data[key];
      }
    });

    // Update with sorted data
    setAllData(sortedData, sortedKeys);
    setStatus({ type: 'success', message: `✅ Sorted ${sortedKeys.length} row(s) by date` });
  };

  /**
   * Export data to CSV
   */
  const exportData = async () => {
    setIsExporting(true);

    try {
      // First save the data
      await save();

      // Then export using the service
      const result = await masterSheetService.export(rowKeys);

      if (result.ok) {
        setStatus({ type: 'success', message: '✅ Export completed successfully!' });
      } else {
        setStatus({ type: 'error', message: `âŒ Export failed: ${result.error}` });
      }
    } catch (error: any) {
      console.error('âŒ Export error:', error);
      setStatus({ type: 'error', message: `âŒ Export failed: ${error.message}` });
    } finally {
      setIsExporting(false);
    }
  };

  // Use already loaded data for Correct Mistake (optimization - no backend call)
  const handleCorrectMistake = () => {
    try {
      // Check if we have any data loaded
      if (rowKeys.length === 0) {
        setStatus({ type: 'warning', message: 'No data available to edit. Please add some data first.' });
        return;
      }

      // Convert current table data to array format for modal
      const dataArray = rowKeys.map(key => ({
        ...data[key],
        entry_date: key,
        id: data[key]?.id || undefined
      }));
setCorrectMistakeData(dataArray);
      setShowCorrectMistake(true);
    } catch (error: any) {
      setStatus({ type: 'error', message: `Error: ${error.message}` });
    }
  };

  /**
   * Aggregate data from other sheets and save to Master Sheet
   */
  const handleAggregateData = async (startDate: string, endDate: string) => {
    // IMMEDIATE CLOSE: Close the modal immediately requesting aggregation
    setShowAggregationModal(false);

    setIsAggregating(true);
    try {
setStatus({ type: 'info', message: `ðŸ”„ Aggregating data from ${startDate} to ${endDate}...` });

      // Call aggregation endpoint
      // Set only_dates_with_data=true to only aggregate dates that have data in other sheets
      const userCompanyId = user?.companyId || (user as any)?.company_id || undefined;
      const result = await masterSheetService.aggregate(startDate, endDate, siteCode, true, userCompanyId ? Number(userCompanyId) : undefined);

      if (result.ok && result.data && result.data.length > 0) {
        // Map aggregated data to table format
        const aggregatedData: any = {};
        const aggregatedKeys: string[] = [];

        result.data.forEach((row: any) => {
          const dateKey = row.entry_date;
          if (dateKey) {
            const mappedRow: any = {
              entry_date: dateKey,
              id: row.id || row.pk || undefined
            };
            MASTER_SHEET_COLUMNS.forEach(col => {
              if (col.key !== 'entry_date') {
                const value = row[col.key];
                mappedRow[col.key] = value !== null && value !== undefined ? String(value) : '';
              }
            });
            aggregatedData[dateKey] = mappedRow;
            if (!aggregatedKeys.includes(dateKey)) {
              aggregatedKeys.push(dateKey);
            }
          }
        });

        // Merge with existing data
        const mergedData = { ...data, ...aggregatedData };
        const mergedKeys = [...new Set([...rowKeys, ...aggregatedKeys])].sort();
        setAllData(mergedData, mergedKeys);

        setStatus({
          type: 'success',
          message: `✅ Successfully aggregated data for ${result.data.length} date(s)!`
        });
      } else {
        setStatus({
          type: 'error',
          message: result.error || 'Failed to aggregate data. Please check if data exists in other sheets.'
        });
      }
    } catch (error: any) {
      console.error('âŒ Error aggregating data:', error);
      setStatus({
        type: 'error',
        message: `âŒ Aggregation failed: ${error.message}`
      });
    } finally {
      setIsAggregating(false);
    }
  };

  // Save corrected data - update all rows that have IDs
  const handleSaveCorrectedData = async (updatedData: MasterSheetDataRow[]) => {
    try {
      if (updatedData.length === 0) {
        throw new Error('No data to save');
      }

      const results: { success: number; failed: number; errors: string[] } = {
        success: 0,
        failed: 0,
        errors: []
      };

      // Process all rows that have IDs
      for (const row of updatedData) {
        if (row.id && typeof row.id === 'number') {
          try {
const updateResult = await masterSheetService.update(row.id, row);
            if (updateResult.ok) {
              results.success++;
} else {
              results.failed++;
              results.errors.push(`Row ${row.id} (${row.entry_date}): ${updateResult.error || 'Update failed'}`);
            }
          } catch (error: any) {
            results.failed++;
            results.errors.push(`Row ${row.id} (${row.entry_date}): ${error.message}`);
          }
        } else {
}
      }

      if (results.success > 0) {
        const message = results.failed > 0
          ? `✅ Updated ${results.success} record(s). ${results.failed} failed.`
          : `✅ Successfully updated ${results.success} record(s)!`;
        setStatus({ type: results.failed > 0 ? 'error' : 'success', message });
        if (results.errors.length > 0) {
          console.error('Update errors:', results.errors);
        }
      } else {
        throw new Error(`Failed to update any records. ${results.errors.join('; ')}`);
      }
    } catch (error: any) {
      throw new Error(`Failed to save: ${error.message}`);
    }
  };


  return (
    <div className="w-full h-full flex flex-col overflow-hidden relative">
      <div className="w-full h-full flex flex-col overflow-hidden">
        {/* Header */}
        <div className="text-white px-3 pt-2 pb-0" style={{ background: 'linear-gradient(to right, var(--color-primary-blue), var(--color-secondary-tata-blue))' }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sheet size={16} />
              <h1 className="text-sm font-bold m-0 leading-none" style={{ color: '#ffffff', fontWeight: 700 }}><strong>Daily Report</strong></h1>
              <p className="text-xs m-0 leading-none" style={{ color: '#ffffff', opacity: 0.95, marginTop: '2px' }}>{siteName}</p>
            </div>
            <FormActions
              isCondensed={isCondensed}
              onToggleView={() => setIsCondensed(!isCondensed)}
              isExporting={isExporting}
              isSaving={isSaving}
              loadingSummary={false}
              hasData={rowKeys.length > 0}
              onAddRow={() => addRow(new Date().toISOString().split('T')[0])}
              onShowSummary={() => { }}
              onSave={save}
              onExport={exportData}
              hideSummary
              customActions={
                <>
                  <DateRangeFilter
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={(start, end) => {
                      setStartDate(start);
                      setEndDate(end);
                    }}
                  />
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setShowAggregationModal(true)}
                    disabled={isAggregating || isSaving || isExporting}
                    className="flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'var(--color-secondary-orange)' }}
                    title="Data Aggregate - Pull data from other sheets and calculate aggregated fields"
                  >
                    {isAggregating ? (
                      <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <Sheet size={12} />
                    )}
                    <span className="hidden sm:inline">Data Aggregate</span>
                  </motion.button>
                  <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleCorrectMistake}
                    disabled={loadingCorrectMistake || isSaving || isExporting}
                    className="flex items-center gap-1 px-3 py-1 rounded text-xs text-white font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ background: 'var(--color-secondary-orange)' }}
                    title="Edit - View and edit your inserted data"
                  >
                    {loadingCorrectMistake ? (
                      <div className="animate-spin h-3 w-3 border-2 border-white border-t-transparent rounded-full"></div>
                    ) : (
                      <Edit size={12} />
                    )}
                    <span className="hidden sm:inline">Edit</span>
                  </motion.button>
                </>
              }
            />
          </div>
        </div>

        {/* Status Banner */}
        {status.type && (
          <StatusBanner
            status={status.type}
            message={status.message}
            onClose={() => setStatus({ type: null, message: '' })}
          />
        )}

        {/* Table */}
        <div className="flex-1 min-h-0" style={{ opacity: isTableVisible ? 1 : 0 }}>
          {isLoadingMoreRef.current && (
            <div className="flex items-center justify-center py-1 text-xs" style={{ color: 'var(--color-primary-blue)' }}>
              <div className="animate-spin h-3 w-3 border-2 border-current border-t-transparent rounded-full mr-2"></div>
              Loading older data...
            </div>
          )}
          <UniversalTable
            columns={MASTER_SHEET_COLUMNS}
            data={data}
            rowKeys={filteredRowKeys}
            styleConfig={DEFAULT_STYLE_CONFIG}
            focusedCell={focusedCell}
            onCellChange={updateCell}
            onRowKeyChange={handleRowKeyChange}  // ✅ Fixed: Use adapter
            onRemoveRowKey={handleRemoveRow}     // ✅ Fixed: Use adapter
            onKeyDown={handleKeyDown}
            onFocus={handleTableFocus}
            setInputRef={registerInput}
            containerRef={containerRef}
          />
        </div>

        {/* Footer */}
        <div className="px-4 py-2 text-xs border-t" style={{ background: 'var(--color-gray-100)', color: '#000000', fontWeight: 400 }}>
          <span>{footerText}</span>
        </div>

        {/* Correct Mistake Modal */}
        {showCorrectMistake && (
          <CorrectMistakeModal
            isOpen={showCorrectMistake}
            onClose={() => setShowCorrectMistake(false)}
            title="Edit - Daily Report"
            data={correctMistakeData}
            columns={MASTER_SHEET_COLUMNS}
            onSave={handleSaveCorrectedData}
            isLoading={loadingCorrectMistake}
            siteCode="axon"
          />
        )}

        {/* Aggregation Modal */}
        <DataAggregationModal
          isOpen={showAggregationModal}
          onClose={() => setShowAggregationModal(false)}
          onAggregate={handleAggregateData}
          isLoading={isAggregating}
        />
      </div>
    </div>
  );
};


